// ─── Lignes prévues & exceptions (Firestore) ──────────────────────
// Cf. CONCEPTION-FINANCE.md. `lignesBudget` porte la PRÉVISION ;
// `occurrencesBudget` ne contient QUE les mois annotés (absent / ignoré /
// montant ajusté) — les occurrences normales sont calculées (services/occurrences).
import { db } from '@mop/core';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, setDoc, getDocs,
  onSnapshot, query, where, writeBatch,
} from 'firebase/firestore';
import { normaliserCredit, echeancierCredit } from './credit.js';

export const FREQUENCES = ['mensuelle', 'trimestrielle', 'semestrielle', 'annuelle', 'ponctuelle'];

export function ecouterLignes(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'lignesBudget'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    l.sort((a, b) => (a.libelle || '').localeCompare(b.libelle || ''));
    callback(l);
  }, (e) => { callback([]); onErreur?.(e); });
}

// Une ligne à CRÉDIT (17/08) n'a ni fréquence ni inflation propres — son
// échéancier d'amortissement EST sa courbe. Deux champs en sont DÉRIVÉS,
// jamais saisis, pour que les cartes qui les lisent directement
// (`EcranConfigBudget.js`, `periode()`…) restent cohérentes avec ce que le
// moteur calculera ensuite :
//   • `montant` (mode « params » seulement — mode « mensualité » : déjà
//     connu, c'est la saisie) = mensualité + assurance de la 1ʳᵉ échéance.
//   • `fin` (TOUJOURS, quel que soit le mode) = mois de la DERNIÈRE
//     échéance — le prêt s'arrête quand le capital est soldé, pas à une
//     date choisie (Cédric, 17/08 : « elle est calculée d'après les
//     paramètres du prêt »).
const corps = (l) => {
  const credit = l.credit?.actif ? normaliserCredit(l.credit) : null;
  const debut = (l.debut || '').slice(0, 7);
  const jour = Math.max(1, Math.min(28, Number(l.jour) || 1));
  let montant = Math.round((parseFloat(String(l.montant).replace(',', '.')) || 0) * 100) / 100;
  let fin = l.fin ? String(l.fin).slice(0, 7) : null;
  if (credit) {
    const echeances = echeancierCredit({ credit, debut, montant, jour })?.echeances || [];
    if (echeances.length) {
      if (credit.mode === 'params') {
        const premiere = echeances[0];
        montant = Math.round(((premiere.mensualite || 0) + (premiere.assurance || 0)) * 100) / 100;
      }
      fin = echeances[echeances.length - 1].mk;
    }
  }
  return {
    budgetId: l.budgetId || null,
    libelle: (l.libelle || '').trim(),
    categorie: l.categorie || 'autre',
    sens: l.sens === 'revenu' ? 'revenu' : 'depense',
    montant,
    debut,
    fin,
    frequence: credit ? 'mensuelle' : (FREQUENCES.includes(l.frequence) ? l.frequence : 'mensuelle'),
    jour,
    inflationTaux: credit ? 0 : (Number(l.inflationTaux) || 0),
    inflationDepuis: credit ? null : (l.inflationDepuis ? String(l.inflationDepuis).slice(0, 7) : null),
    actif: l.actif !== false,
    credit,
  };
};

export async function creerLigne({ espaceId, calendrierId, creePar, ...l }) {
  const b = corps(l);
  if (!b.libelle) throw new Error('Donne un libellé à la ligne.');
  // Mode « params » : c'est le CAPITAL qui doit être valide, pas le montant
  // (dérivé ci-dessus — il peut légitimement être 0 le mois d'un déblocage
  // pas encore commencé).
  if (b.credit?.mode === 'params') {
    if (!b.credit.capital) throw new Error('Capital du crédit invalide');
  } else if (!b.montant || b.montant <= 0) throw new Error('Montant invalide');
  if (!/^\d{4}-\d{2}$/.test(b.debut)) throw new Error('Mois de début invalide');
  const ref = await addDoc(collection(db, 'lignesBudget'), {
    espaceId, ...(calendrierId ? { calendrierId } : {}), ...b,
    creePar: creePar || null, creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export const modifierLigne = (id, l) => updateDoc(doc(db, 'lignesBudget', id), corps(l));

// Supprimer une ligne : on détache ses actuals (ils redeviennent des imprévus,
// on ne les efface JAMAIS) et on retire ses exceptions.
export async function supprimerLigne(id, espaceId) {
  const [ops, exc] = await Promise.all([
    getDocs(query(collection(db, 'depenses'), where('espaceId', '==', espaceId))),
    getDocs(query(collection(db, 'occurrencesBudget'), where('espaceId', '==', espaceId))),
  ]);
  const batch = writeBatch(db);
  ops.docs.filter(d => d.data().ligneId === id).forEach(d => batch.update(d.ref, { ligneId: null }));
  exc.docs.filter(d => d.data().ligneId === id).forEach(d => batch.delete(d.ref));
  batch.delete(doc(db, 'lignesBudget', id));
  await batch.commit();
}

// ── Exceptions d'occurrence ──────────────────────────────────────
export function ecouterExceptions(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'occurrencesBudget'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (e) => { callback([]); onErreur?.(e); });
}

// Id DÉTERMINISTE (ligne + mois) : une occurrence n'a qu'une annotation, et
// deux postes qui annotent en même temps convergent au lieu de dupliquer.
const idExc = (ligneId, mois) => `${ligneId}_${mois}`;

export async function definirException({ espaceId, calendrierId, ligneId, mois, statut, montantAjuste, note, creePar }) {
  await setDoc(doc(db, 'occurrencesBudget', idExc(ligneId, mois)), {
    espaceId, ...(calendrierId ? { calendrierId } : {}),
    ligneId, mois,
    ...(statut ? { statut } : {}),
    ...(montantAjuste != null ? { montantAjuste: Math.round(montantAjuste * 100) / 100 } : {}),
    ...(note ? { note } : {}),
    creePar: creePar || null, majLe: new Date().toISOString(),
  }, { merge: true });
}

// Retirer l'annotation → l'occurrence redevient purement calculée.
export const retirerException = (ligneId, mois) => deleteDoc(doc(db, 'occurrencesBudget', idExc(ligneId, mois)));

// ── Rattachement d'un actual à une ligne prévue ──────────────────
// `moisImputation` permet de rattacher au bon mois une opération payée en
// décalé (loyer de mars prélevé le 28/02).
export const rattacherActual = (opId, ligneId, moisImputation) =>
  updateDoc(doc(db, 'depenses', opId), { ligneId: ligneId || null, ...(moisImputation ? { moisImputation } : {}) });

export const detacherActual = (opId) => updateDoc(doc(db, 'depenses', opId), { ligneId: null });
